import React, { useState, useEffect } from 'react';
import { History } from 'history';
import { ProgressBar } from '@guildeducationinc/recess';
import {
	MemberExperienceUserFromIdToken,
	useMemberExperienceAuth,
} from '@guildeducationinc/auth-frontend-tools';
import { ErrorToast } from 'src/components/Quiz/ErrorToast';
import { Header } from 'src/components/Quiz/Header';
import { SEO } from 'src/components/SEO/SEO';
import { useQuiz, useQuizDispatch } from '@src/components/Providers/QuizProvider';
import { useRecommendations } from '@src/components/Providers/RecommendationsProvider';
import classNames from 'classnames';
import { trackEvent } from '@src/services/analytics';
import { useCreateRecommendationsAndQuizResponsesMutation } from '@src/graphqlTypes/catalogServiceTypes';
import { toOfferingHit } from '@src/utilities/offeringHitsUtility';
import { useSearchContext } from '@src/components/Providers/SearchProvider';
import { datadogRum } from '@datadog/browser-rum';
import { AnswerKeys, Answers, Screens } from './types';
import { formatResponses, getCurrentRecommendations, updateAnswers } from './answerHelpers';
import { screens } from './screenMapping';
import { getNextScreen } from './screenHelpers';
import styles from './QuizContainer.module.scss';

interface RouterProps {
	history: History;
}

export const QuizContainer: React.FC<RouterProps> = props => {
	const { answers, screenHistory, screenMapping, sessionRecs } = useQuiz();
	const { dispatch } = useQuizDispatch();
	const { userFromIdToken: authUser } = useMemberExperienceAuth();
	const { filters, index, planUuid } = useSearchContext();
	const [createRecsMutation] = useCreateRecommendationsAndQuizResponsesMutation();
	const { recommendationsData, setRecommendationsData } = useRecommendations();

	const [animation, setAnimation] = useState<string>('none');
	const [showErrorToast, toggleShowErrorToast] = useState<boolean>(false);

	const defaultAnswers = recommendationsData?.recommendationQuizResponses || {};
	const Screen = screens[screenHistory.slice(-1)[0]];
	const currentScreen = screenHistory[screenHistory.length - 1];
	const totalScreens = Object.keys(screens).length;
	const exitRef = React.useRef<HTMLElement>(null);

	useEffect(() => {
		trackEvent('ProgramQuizStart', { employerUuid: authUser?.employer_uuid });
	}, [authUser]);

	const handleSetScreenHistory = (newScreenHistory: Screens[]) => {
		dispatch({
			type: 'updated_screen_history',
			screenHistory: newScreenHistory,
		});
	};

	const handleNextScreen = async () => {
		const recommendations = await getCurrentRecommendations(answers, authUser.id, index, filters);
		dispatch({
			type: 'set_session_recs',
			sessionRecs: recommendations.map(id => parseInt(id)),
		});

		let nextScreen: Screens;
		if (recommendations.length <= 3) {
			nextScreen = Screens.LOADING;
		} else {
			nextScreen = getNextScreen(answers, authUser.employer_uuid, currentScreen, screenMapping);
		}

		const newHistory = screenHistory.concat([nextScreen]);

		setAnimation('out');
		exitRef.current.focus();
		setTimeout(() => {
			handleSetScreenHistory(newHistory);
			setAnimation('in');
			window.scrollTo(0, 0);
			setTimeout(() => setAnimation('none'), 750);
		}, 800);
	};

	const handlePreviousScreen = () => {
		const newHistory = screenHistory.slice(0, screenHistory.length - 1);
		handleSetScreenHistory(newHistory);
		exitRef.current.focus();
	};

	const handleUpdateAnswers = (answer: Partial<Answers>, answers: Answers) => {
		const newAnswers = updateAnswers(answer, answers);
		dispatch({ type: 'updated_answers', answers: newAnswers });
	};

	const createRecs = async () => {
		const noRecommendations = sessionRecs.length <= 0;
		if (noRecommendations) {
			datadogRum.addAction('QuizCompletedWithNoRecommendations', {
				planUuid,
				answers,
				sessionRecs,
			});
		}

		trackEvent('ProgramQuizCompleted', { offeringIDs: sessionRecs.toString() });

		const interests = answers.interests ? answers.interests : [];
		const businessInterests = answers.businessInterests ? answers.businessInterests : [];
		const techInterests = answers.techInterests ? answers.techInterests : [];
		const healthcareInterests = answers.healthcareInterests ? answers.healthcareInterests : [];
		const allInterests = [
			...interests,
			...businessInterests,
			...techInterests,
			...healthcareInterests,
		];
		const finalAnswers = {
			...answers,
			...{ [AnswerKeys.interests]: allInterests },
		};

		try {
			const recommendedPrograms = await createRecsMutation({
				variables: {
					recommendationsInput: {
						programIds: sessionRecs,
						quizResponses: formatResponses(finalAnswers, screenHistory),
					},
				},
				fetchPolicy: 'no-cache',
			});

			setRecommendationsData({
				recommendations:
					recommendedPrograms.data.createRecommendationsAndQuizResponses.map(toOfferingHit),
				recommendationQuizResponses: {
					feeling: answers.feeling,
					goal: answers.goal,
					education: answers.education,
					timeToComplete: answers.timeToComplete,
					weeklyTime: answers.weeklyTime,
					collegeGpa: answers.collegeGpa,
					hsGpa: answers.hsGpa,
					credits: answers.credits,
					interests,
					businessInterests,
					techInterests,
					healthcareInterests,
				},
			});

			// Wait at least 3 seconds before redirecting so it seems like it's really working
			await new Promise(resolve => setTimeout(resolve, 3000));
			props.history.replace('/allprograms#quiz-results');
			dispatch({ type: 'reset_quiz_state' });
		} catch (err) {
			toggleShowErrorToast(true);
		}
	};

	return (
		<div className={styles.quizContainer}>
			<SEO title='Recommendation Quiz' />
			<Header allowExit={!!screenHistory.length} exitRef={exitRef} />
			<div className={styles.quizScreen}>
				<ProgressBar
					animate
					currentProgress={screenHistory.length}
					testid='quiz-screen'
					totalProgress={totalScreens}
				/>
				<div className={classNames([styles.animate, styles[animation], styles.screenContent])}>
					<ErrorToast
						actionCopy='Exit to catalog'
						destinationLink='/allprograms'
						display={showErrorToast}
						errorCopy='We had an issue finalizing your recommendations.'
					/>
					<Screen
						createRecs={createRecs}
						defaultAnswers={defaultAnswers}
						nextScreen={handleNextScreen}
						previousScreen={handlePreviousScreen}
						updateAnswers={handleUpdateAnswers}
						user={authUser}
					/>
				</div>
				<div className={styles.quizFooter} id='quiz-footer' />
			</div>
		</div>
	);
};
